import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorSnackbarAlert from "../../../components/element/ErrorSnackbarAlert";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import useFBOApi from "../../../services/api/useFBOApi";
import { StorageContext } from "../../../services/StorageProvider";
import ROUTE_PATHS from "../../../utils/routePaths";
import { AIRPORT_TYPES } from "../../../utils/constants";
import "../CSS/CreateFlightPlan.css";
import CreateFlightPlanTable from "../Tables/CreateFlightPlanTable";
import CreateFlightPlanHeader from "../Views/CreateFlightPlanHeader";

const CreateFlightPlan = () => {
  const navigate = useNavigate();
  const { fboUserProfileData, airlineListData, airportListData } = useContext(StorageContext);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState({});
  const [selectedOrigin, setSelectedOrigin] = useState({});
  const [selectedDestination, setSelectedDestination] = useState({});
  const [filteredDestinationAirports, setFilteredDestinationAirports] = useState([]);
  const { createManualFlightPlan } = useFBOApi();

  const [flightDetails, setFlightDetails] = useState({
    userId: "",
    airlineId: "",
    aircraftTypeId: "",
    iataFlightNumber: "",
    icaoFlightNumber: "",
    flightNumber: "",
    aircraftRegistrationNumber: "",
    departureAirportId: "",
    arrivalAirportId: "",
    departureTime: "",
    arrivalTime: "",
    requiresArrivalOvertime: false,
    requiresDepartureOvertime: false,
  });

 
  const manualFlightPlanMutation = useMutation({
    mutationFn: ({ data }) => createManualFlightPlan(data),
    onSuccess: () => {
      navigate(ROUTE_PATHS.LANDING_FEE_DATA);
    },
  });

 
  // Filter destination airports based on FBO associations
  useEffect(() => {
    if (airportListData && fboUserProfileData?.associations?.[0]?.fboId) {
      const filteredAirports = airportListData.filter((airport) =>
        airport.terminals?.some(
          (terminal) =>
            terminal.fboId === fboUserProfileData.associations[0].fboId
        )
      );
      setFilteredDestinationAirports(filteredAirports);

      // Auto-select destination airport if only one option is available
      if (filteredAirports.length === 1 && !selectedDestination.id) {
        const singleAirport = filteredAirports[0];
        setSelectedDestination(singleAirport);
        
        // Auto-select terminal if only one is available
        const filteredTerminals = singleAirport.terminals?.filter(
          (terminal) =>
            terminal.fboId === fboUserProfileData.associations[0].fboId
        );
        
        setFlightDetails((prev) => {
          const updated = {
            ...prev,
            arrivalAirportId: singleAirport.id,
          };
          
          if (filteredTerminals?.length === 1) {
            updated.arrivalAirportTerminalId = filteredTerminals[0].id;
          }
          
          return updated;
        });
      }
    }
  }, [airportListData, fboUserProfileData, selectedDestination.id]);

  useEffect(() => {
    if (fboUserProfileData?.id) {
      setFlightDetails((prev) => ({
        ...prev,
        userId: fboUserProfileData.id,
      }));
    }

    if (airlineListData?.data?.length === 1) {
      setSelectedAirline(airlineListData?.data[0]);
      setFlightDetails((prev) => ({
        ...prev,
        airlineId: airlineListData?.data[0]?.id,
      }));
    }
  }, [airlineListData, fboUserProfileData]);

  const handleBackButton = () => {
    navigate(ROUTE_PATHS.LANDING_FEE_DATA);
  };

  const handleErrorPopupShow = (error) => {
    setShowErrorPopup(true);
    setErrorMessage(error);
  };

  const handleErrorPopupClose = () => {
    setShowErrorPopup(false);
  };

  const handleSaveButton = () => {
    const currentDateTime = dayjs();

    if (selectedOrigin.id === selectedDestination.id) {
      handleErrorPopupShow("Origin and Destination cannot be the same.");
    } else if (
      selectedOrigin.airportType !== AIRPORT_TYPES.DOMESTIC &&
      selectedDestination.airportType !== AIRPORT_TYPES.DOMESTIC
    ) {
      handleErrorPopupShow("At least one airport type should be domestic.");
    } else if (flightDetails.departureTime === flightDetails.arrivalTime) {
      handleErrorPopupShow(
        "Arrival time and Departure time cannot be the same."
      );
    } else {
      // Prepare data for manual flight plan API
      const manualFlightPlanData = {
        airlineId: flightDetails.airlineId,
        aircraftTypeId: flightDetails.aircraftTypeId,
        flightNumber: flightDetails.flightNumber,
        aircraftRegistrationNumber: flightDetails.aircraftRegistrationNumber,
        departureAirportId: flightDetails.departureAirportId,
        arrivalAirportId: flightDetails.arrivalAirportId,
        departureAirportTerminalId: flightDetails.departureAirportTerminalId || null,
        arrivalAirportTerminalId: flightDetails.arrivalAirportTerminalId || null,
        departureTime: flightDetails.departureTime,
        arrivalTime: flightDetails.arrivalTime,
        requiresArrivalOvertime: false,
        requiresDepartureOvertime: false,
      };

      // Remove null values
      Object.keys(manualFlightPlanData).forEach(key => {
        if (manualFlightPlanData[key] === null || manualFlightPlanData[key] === "") {
          delete manualFlightPlanData[key];
        }
      });

      manualFlightPlanMutation.mutate({
        data: manualFlightPlanData,
      });
    }
  };

 
  const validateFlightDetails = () => {
    if (
      flightDetails.aircraftTypeId === "" ||
      flightDetails.flightNumber.trim() === "" ||
      flightDetails.departureAirportId === "" ||
      flightDetails.arrivalAirportId === "" ||
      flightDetails.departureTime === "" ||
      flightDetails.arrivalTime === ""
    ) {
      return false;
    }
    return true;
  };

  return (
    <div className="create-flight-plan-container">
     
      {manualFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {manualFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <CreateFlightPlanHeader
        isValid={validateFlightDetails()}
        onClickBack={handleBackButton}
        onClickSave={handleSaveButton}
        isLoading={manualFlightPlanMutation.isPending}
      />
      <CreateFlightPlanTable
        flightDetails={flightDetails}
        airlineData={airlineListData}
        setFlightDetails={setFlightDetails}
        selectedAirline={selectedAirline}
        setSelectedAirline={setSelectedAirline}
        selectedOrigin={selectedOrigin}
        setSelectedOrigin={setSelectedOrigin}
        selectedDestination={selectedDestination}
        setSelectedDestination={setSelectedDestination}
        fboUserProfileData={fboUserProfileData}
        filteredDestinationAirports={filteredDestinationAirports}
      />
      {/* <OvertimeDialog
        flightDetails={flightDetails}
        origin={selectedOrigin}
        destination={selectedDestination}
        open={showOvertimeDialog}
        onClose={() => setShowOvertimeDialog(false)}
        onAccept={handleAcceptButton}
      /> */}
      <ErrorSnackbarAlert open={showErrorPopup} onClose={handleErrorPopupClose}>
        {errorMessage}
      </ErrorSnackbarAlert>
    </div>
  );
};

export default CreateFlightPlan;
